import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import { setCsrfToken } from '@/api/http/interceptors'
import type { ApiResponse } from '@/api/http/types'
import {
  getPublicAuthApexHostname,
  IdentityAuthPath,
  POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT,
  abortApexPurge,
  readPostLogoutSkipAuthApexRedirectFlag,
  clearLastTenantSubdomain,
  clearTenantHandoffFailedFlag,
  clearTenantHandoffStarted,
} from '../lib'
import {
  captureSmUserSessionStarted,
  identifySmUser,
} from '@/lib/analytics'
import { mapIdentityRoleToSensemaker, isProviderRole } from '../lib/map-identity-role'
import { resolveMeSubdomain } from '../lib/request-subdomain'

/** Raw `/api/v1/auth/me` user payload (identity service). */
export interface IdentityMeUser {
  id: string
  email: string
  name: string
  role: string
  status: string
  permissions?: string[]
  subdomain?: string | null
  /** Login success `loggedUser.tenantSubdomain` — same meaning as `subdomain`. */
  tenantSubdomain?: string | null
  /** Tenant UUID from identity JWT/session when scoped to a tenant */
  tenant_id?: string | null
  csrf_token?: string
  /** Sensemaker dashboard first-time tooltip tour (tenant user only). */
  sensemaker_ftux_status?: 'PENDING' | 'COMPLETED' | 'SKIPPED' | null
  firstName?: string | null
  lastName?: string | null
  /** BFP-1006 — self-serve signup persona. */
  persona?: 'individual' | 'team' | 'company' | null
  tenantName?: string | null
  industry?: string | null
  companySize?: string | null
  /** Platform admin must change seeded password on first login (BFP-517). */
  requiresPasswordReset?: boolean
}

const meRequestConfig = { skipUnauthorizedRedirect: true } as const

export interface FetchAndApplySessionOptions {
  /** Set during an active login so post-logout guards do not block `/me`. */
  ignoreLogoutGuard?: boolean
}

export async function fetchAndApplySession(
  options?: FetchAndApplySessionOptions,
): Promise<IdentityMeUser> {
  const { setUser, explicitLogout } = useSessionStore.getState()
  const ignoreLogoutGuard = options?.ignoreLogoutGuard === true

  if (
    !ignoreLogoutGuard &&
    (explicitLogout || readPostLogoutSkipAuthApexRedirectFlag())
  ) {
    throw new Error('SESSION_DECLINED_AFTER_LOGOUT')
  }

  const { data: envelope } = await apiClient.get<ApiResponse<IdentityMeUser>>(
    IdentityAuthPath.ME,
    meRequestConfig
  )

  const raw = envelope.data
  const role = mapIdentityRoleToSensemaker(raw.role, raw.permissions)

  // Reject Signal Provider users and any other unrecognised role.
  // Cookies on localhost are not port-scoped, so a Provider session cookie
  // will be sent to this app's /me call — we must refuse it here rather than
  // mapping it to VIEWER (fail-closed, not fail-open).
  if (role === null || isProviderRole(raw.role)) {
    throw new Error('WRONG_APP: Signal Provider sessions are not valid in Sensemaker')
  }

  if (raw.csrf_token) {
    setCsrfToken(raw.csrf_token)
  }

  const subdomain = resolveMeSubdomain(raw, role)

  setUser({
    id: raw.id,
    email: raw.email,
    name: raw.name,
    role,
    tenantId: raw.tenant_id ?? null,
    subdomain,
    requiresPasswordReset: Boolean(raw.requiresPasswordReset),
  })

  identifySmUser(raw.id)
  captureSmUserSessionStarted({
    sm_user_id: raw.id,
    session_source: ignoreLogoutGuard ? 'login' : 'session_restore',
    tenant_id: raw.tenant_id ?? null,
  })

  if (ignoreLogoutGuard) {
    try {
      globalThis.sessionStorage?.removeItem(POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT)
      clearTenantHandoffFailedFlag()
      // A stale apex-origin handoff marker (never cleared here because the
      // success clear runs on the *tenant* origin) would otherwise trip
      // `isTenantHandoffBounceLoop()` in TenantHostRedirect and abort the
      // subdomain redirect. Reset it so each explicit login starts clean.
      clearTenantHandoffStarted()
    } catch {}
  }

  return { ...raw, subdomain }
}

export function useMe() {
  const explicitLogout = useSessionStore((s) => s.explicitLogout)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)
  const authTransitionInProgress = useSessionStore((s) => s.authTransitionInProgress)
  const postLogoutBootstrapPending = readPostLogoutSkipAuthApexRedirectFlag()

  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: () => fetchAndApplySession(),
    retry: false,
    /** Bootstrap only — login mutation already calls `/me` and `setUser`. */
    enabled:
      !explicitLogout &&
      !isAuthenticated &&
      !authTransitionInProgress &&
      !postLogoutBootstrapPending,
  })
}

type LoginSuccessData = {
  /** Identity merges trial/tenant extras with snake_case keys. */
  subdomain?: string | null
  loggedUser?: {
    tenantSubdomain?: string | null
  }
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: {
      email: string
      password: string
      remember_me: boolean
    }): Promise<IdentityMeUser> => {
      const { setAuthTransitionInProgress, setUser } = useSessionStore.getState()
      setAuthTransitionInProgress(true)
      abortApexPurge()
      try {
        const { data: loginEnvelope } = await apiClient.post<
          ApiResponse<LoginSuccessData>
        >(IdentityAuthPath.LOGIN, body, { skipUnauthorizedRedirect: true })

        const me = await fetchAndApplySession({ ignoreLogoutGuard: true })

        // Prefer `/me.subdomain`; fall back to login body so TenantHostRedirect
        // always has the tenant DNS label after a successful Sensemaker login.
        const fromLogin =
          loginEnvelope.data?.subdomain?.trim() ||
          loginEnvelope.data?.loggedUser?.tenantSubdomain?.trim() ||
          ''
        if (!me.subdomain && fromLogin) {
          const current = useSessionStore.getState().user
          if (current) {
            setUser({ ...current, subdomain: fromLogin })
          }
          return { ...me, subdomain: fromLogin }
        }

        return me
      } finally {
        useSessionStore.getState().setAuthTransitionInProgress(false)
      }
    },
    onSuccess: (me) => {
      queryClient.setQueryData(queryKeys.auth.me(), me)
    },
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (body: { email: string }) => {
      await apiClient.post<ApiResponse<unknown>>(
        IdentityAuthPath.PASSWORD_RESET_REQUEST,
        body,
        { skipUnauthorizedRedirect: true }
      )
    },
  })
}

export function useValidateResetToken(token: string | null) {
  return useQuery({
    queryKey: queryKeys.auth.validateResetToken(token ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<unknown>>(
        `${IdentityAuthPath.PASSWORD_RESET_VALIDATE}/${token}`,
        { skipUnauthorizedRedirect: true }
      )
      return data
    },
    enabled: !!token,
    retry: false,
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (body: {
      token: string
      new_password: string
      confirm_password: string
    }) => {
      await apiClient.post<ApiResponse<unknown>>(
        IdentityAuthPath.PASSWORD_RESET,
        body,
        { skipUnauthorizedRedirect: true }
      )
    },
  })
}

export async function patchSensemakerFtuxStatus(
  status: 'COMPLETED' | 'SKIPPED'
): Promise<void> {
  await apiClient.patch<ApiResponse<IdentityMeUser>>(
    IdentityAuthPath.ME_SENSEMAKER_FTUX,
    { status }
  )
}

export function usePatchSensemakerFtux() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: patchSensemakerFtuxStatus,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
    },
  })
}

export function usePlatformAdminInitialPassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: { newPassword: string; confirmPassword: string }) => {
      await apiClient.post<ApiResponse<{ passwordUpdated: boolean }>>(
        IdentityAuthPath.ME_PLATFORM_ADMIN_INITIAL_PASSWORD,
        body,
      )
    },
    onSuccess: () => {
      // Server bumps tokenVersion — prior login cookies are invalid. Clear client
      // state so the success screen and /login handoff match other password flows.
      setCsrfToken(null)
      useSessionStore.getState().clearSession()
      queryClient.clear()
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const clearSession = useSessionStore((s) => s.clearSession)

  return useMutation({
    mutationFn: async () => {
      const { setAuthTransitionInProgress } = useSessionStore.getState()
      setAuthTransitionInProgress(true)
      abortApexPurge()
      try {
        await apiClient.post<ApiResponse<unknown>>(IdentityAuthPath.LOGOUT)
      } finally {
        useSessionStore.getState().setAuthTransitionInProgress(false)
      }
    },
    onSettled: () => {
      try {
        globalThis.sessionStorage?.setItem(
          POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT,
          '1',
        )
      } catch {}
      // Handoff markers are origin-scoped; clear them on logout so a later
      // Platform Admin ↔ Sensemaker switch cannot inherit a stale bounce window.
      clearTenantHandoffStarted()
      clearTenantHandoffFailedFlag()
      // Clears apex localStorage when logout runs on apex; tenant-host logout
      // clears the pointer in RequireGuest's post-logout apex purge.
      clearLastTenantSubdomain()
      setCsrfToken(null)
      clearSession()
      queryClient.clear()
      const loc = globalThis.window?.location
      if (loc && !loc.pathname.startsWith('/login')) {
        const apex = getPublicAuthApexHostname(loc.hostname)
        const portSuffix = loc.port ? `:${loc.port}` : ''
        loc.replace(`${loc.protocol}//${apex}${portSuffix}/login`)
      }
    },
  })
}
